import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { OrderFormFieldInterface, OrderFormFieldOptionInterface } from '@galaxy/marketplace-apps/v1';
import { BUSINESS_USER, CHECK_BOX, ControlType, DROP_DOWN, FILES, TEXT_AREA, TEXT_BOX } from '../field-base';

@Component({
  selector: 'store-field-builder',
  templateUrl: './field-builder.component.html',
  styleUrls: ['./field-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldBuilderComponent implements OnInit {
  @Input() orderFormFieldGroup: UntypedFormGroup;
  @Input() supportedFieldTypes: FormFieldInterface[];
  @Output() removeField: EventEmitter<string> = new EventEmitter(null);

  showValidators: boolean;
  showSuffixes: boolean;

  static createDropDownOption(option: OrderFormFieldOptionInterface, required: boolean): UntypedFormGroup {
    return new UntypedFormGroup({
      label: new UntypedFormControl(option.label || option.value, required ? [Validators.required] : []),
      value: new UntypedFormControl(option.value),
      useValue: new UntypedFormControl(!!option.value),
    });
  }

  static createDropDownOptions(options: OrderFormFieldOptionInterface[]): UntypedFormArray {
    if (options === null || options === undefined) {
      options = [];
    }
    const orderFormFieldsGroupArray: UntypedFormGroup[] = options.map((option) =>
      FieldBuilderComponent.createDropDownOption(option, false),
    );
    return new UntypedFormArray(orderFormFieldsGroupArray);
  }

  static createOrderFormFieldGroup(orderFormField: OrderFormFieldInterface): UntypedFormGroup {
    const orderFormFieldGroup = new UntypedFormGroup({
      descriptionControl: new UntypedFormControl(orderFormField.description, []),
      labelControl: new UntypedFormControl(orderFormField.label, [Validators.required]),
      idControl: new UntypedFormControl(orderFormField.id, [
        Validators.required,
        Validators.pattern("^[\\w\\s-$&`:?/.,()+*'’|]+$"),
      ]),
      requiredControl: new UntypedFormControl(orderFormField.required || false, []),
      uploadUrlControl: new UntypedFormControl(orderFormField.uploadUrl, []),
      prefixControl: new UntypedFormControl(orderFormField.prefix, []),
      suffixControl: new UntypedFormControl(orderFormField.suffix, []),
      regexValidatorControl: new UntypedFormControl(orderFormField.regexValidator, regexValidator()),
      regexErrorMessageControl: new UntypedFormControl(orderFormField.regexErrorMessage, []),
      typeControl: new UntypedFormControl(getFieldInterface(orderFormField.type), [Validators.required]),
      allowMultiples: new UntypedFormControl(orderFormField.allowMultiples || false, []),
      allowDuplicates: new UntypedFormControl(orderFormField.allowDuplicates || false, []),
      maxChoices: new UntypedFormControl(orderFormField.maxChoices ? orderFormField.maxChoices : 1, [
        Validators.min(1),
        numberValidator(),
      ]),
      forOfficeUseOnly: new UntypedFormControl(orderFormField.forOfficeUseOnly || false, []),
      officeEditableOnly: new UntypedFormControl(orderFormField.officeEditableOnly || false, []),
    });
    if (orderFormField.optionsWithLabels) {
      const optionsArray = FieldBuilderComponent.createDropDownOptions(orderFormField.optionsWithLabels);
      orderFormFieldGroup.addControl('optionsFormArray', optionsArray);
    } else if (orderFormField.options) {
      const optionsArray = FieldBuilderComponent.createDropDownOptions(
        orderFormField.options.map((option) => ({ label: option })),
      );
      orderFormFieldGroup.addControl('optionsFormArray', optionsArray);
    } else {
      orderFormFieldGroup.addControl('optionsFormArray', new UntypedFormArray([]));
    }

    return orderFormFieldGroup;
  }

  ngOnInit(): void {
    if (!this.supportedFieldTypes || this.supportedFieldTypes.length < 1) {
      this.supportedFieldTypes = FORM_FIELDS;
    }
    this.initForm();
  }

  initForm(): void {
    // Check to see if form has been initiated
    if (!this.orderFormFieldGroup.get('labelControl')) {
      this.orderFormFieldGroup = FieldBuilderComponent.createOrderFormFieldGroup({});
    }
  }

  getOptionsArray(): UntypedFormArray {
    return this.orderFormFieldGroup.get('optionsFormArray') as UntypedFormArray;
  }

  getCurrentFieldInterface(): string {
    const formFieldTypeInterface = this.orderFormFieldGroup.get('typeControl').value as FormFieldInterface;
    return formFieldTypeInterface ? formFieldTypeInterface.id : null;
  }

  createIdFromLabel(): void {
    const idControlValue = this.orderFormFieldGroup.get('idControl').value;
    if (!idControlValue || idControlValue === '') {
      const newId = convertLabelInputIntoId(this.orderFormFieldGroup.get('labelControl').value);
      this.orderFormFieldGroup.get('idControl').setValue(newId);
    }
  }

  removeOption(index: number): void {
    const formArray = this.getOptionsArray();
    formArray.removeAt(index);
  }

  addOption(): void {
    const fieldArray = this.getOptionsArray();
    fieldArray.push(FieldBuilderComponent.createDropDownOption({ label: '' }, true));
  }

  getFormValues(): OrderFormFieldInterface {
    const obj: OrderFormFieldInterface = {};
    obj.label = this.orderFormFieldGroup.get('labelControl').value;
    obj.id = this.orderFormFieldGroup.get('idControl').value;

    const formFieldType = this.orderFormFieldGroup.get('typeControl').value as FormFieldInterface; // use the id instead of the whole object
    obj.type = formFieldType ? formFieldType.id : null;

    // Options will be nested under their own form control in the larger Orderformfield array
    const optionsArray = this.orderFormFieldGroup.get('optionsFormArray') as UntypedFormArray;
    if (optionsArray && optionsArray.value) {
      obj.options = optionsArray.value.map((option) => option.value || option.label);
      obj.optionsWithLabels = optionsArray.value.map((option) => ({ label: option.label, value: option.value }));
    }

    obj.description = this.orderFormFieldGroup.get('descriptionControl').value;
    obj.required = this.orderFormFieldGroup.get('requiredControl').value || false;
    obj.uploadUrl = this.orderFormFieldGroup.get('uploadUrlControl').value;
    obj.prefix = this.orderFormFieldGroup.get('prefixControl').value;
    obj.suffix = this.orderFormFieldGroup.get('suffixControl').value;
    obj.regexValidator = this.orderFormFieldGroup.get('regexValidatorControl').value;
    obj.regexErrorMessage = this.orderFormFieldGroup.get('regexErrorMessageControl').value;
    obj.allowMultiples = this.orderFormFieldGroup.get('allowMultiples').value || false;
    obj.allowDuplicates = this.orderFormFieldGroup.get('allowDuplicates').value || false;
    obj.maxChoices = this.orderFormFieldGroup.get('maxChoices').value || 1;
    obj.forOfficeUseOnly = this.orderFormFieldGroup.get('forOfficeUseOnly').value || false;
    obj.officeEditableOnly = this.orderFormFieldGroup.get('officeEditableOnly').value || false;
    return obj;
  }
}

// Validation to ensure supplied regex is valid
export function regexValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    try {
      new RegExp(control.value || '');
    } catch (e) {
      return { regex: true };
    }
    return null;
  };
}

export function numberValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const isNumber = /^\d+$/;
    if (isNumber.test(control.value)) {
      return null;
    }
    return { number: true };
  };
}

export interface FormFieldInterface {
  id: ControlType;
  name: string;
}

export const FORM_FIELDS: FormFieldInterface[] = [
  { id: FILES, name: 'FRONTEND.STORE.ORDER_FORM.FIELD_TYPES.FILES' },
  { id: DROP_DOWN, name: 'FRONTEND.STORE.ORDER_FORM.FIELD_TYPES.DROP_DOWN' },
  { id: CHECK_BOX, name: 'FRONTEND.STORE.ORDER_FORM.FIELD_TYPES.CHECK_BOX' },
  { id: TEXT_AREA, name: 'FRONTEND.STORE.ORDER_FORM.FIELD_TYPES.TEXT_AREA' },
  { id: TEXT_BOX, name: 'FRONTEND.STORE.ORDER_FORM.FIELD_TYPES.TEXT_BOX' },
  { id: BUSINESS_USER, name: 'FRONTEND.STORE.ORDER_FORM.FIELD_TYPES.END_USER' },
];

export function getFieldInterface(formFieldId: string): FormFieldInterface {
  return getFormFieldInterfaceFromId(formFieldId as ControlType);
}

export function getFormFieldInterfaceFromId(id: ControlType): FormFieldInterface {
  return FORM_FIELDS.find((obj: FormFieldInterface) => obj.id === id);
}

export function convertLabelInputIntoId(labelValue: string): string {
  let newId = labelValue.toLowerCase();
  newId = newId.replace(/[ -]/g, '_');
  newId = newId.replace(/[^0-9a-z_]/g, '');
  return newId;
}
